/**
 * Forge overlay — merge same-type, same-tier items into a higher-tier result.
 * Opened from the base screen when the Forge building is built (level 1-3).
 */
import type { FederatedPointerEvent } from 'pixi.js';
import { Container, Graphics, Text } from 'pixi.js';
import { draw3DButton, drawBevelPanel, drawHeaderBand, drawInsetRect, GUGI_SHADOW, buildCloseButton, type OverlayResult } from './PanelUtils';
import { ACCENT, ACCENT_DIM, ACCENT_DARK } from './LayoutConstants';
import { buildUpgradeCell, buildWeaponCell, createUpgradeTooltip, createWeaponTooltip, positionTooltip } from './InventoryUI';
import { getUpgradeById, ALL_UPGRADES } from '../upgrades/UpgradeData';
import { getWeaponById, ALL_WEAPONS } from '../upgrades/WeaponData';
import { type ScoredItem } from '../upgrades/Quality';
import { generateItemName } from '../upgrades/ItemNames';
import { type GameStateManager } from '../states/GameStateManager';
import { isWeaponItem } from '../upgrades/TraderSystem';
import { createScrollBar, SCROLLBAR_WIDTH } from './ScrollBar';
import { attachScrollDrag, type ScrollDragHandle } from './ScrollablePanel';
import { trackForge } from '../achievements/Hooks';

const FORGE_COLOR = 0xff6622;

// ─── Layout constants ───────────────────────────────────────
const PANEL_W    = 650;
const PANEL_H    = 520;
const SLOT_SIZE  = 72;
const INV_COLS   = 7;
const CELL_SIZE  = 60;
const CELL_GAP   = 4;

// ─── Public interface ───────────────────────────────────────

/** Forge overlay always provides an `update` hook (sparkle timer, slot preview). */
export interface ForgeOverlayResult extends OverlayResult {
  update: (dt: number) => void;
}

export function buildForgeOverlay(
  manager: GameStateManager,
  screenW: number,
  screenH: number,
  onClose: () => void,
): ForgeOverlayResult {
  const root = new Container();

  // ── Backdrop ────────────────────────────────────────────
  const backdrop = new Graphics();
  backdrop.rect(0, 0, screenW, screenH);
  backdrop.fill({ color: 0x000000, alpha: 0.6 });
  backdrop.eventMode = 'static';
  backdrop.cursor = 'default';
  backdrop.on('pointerdown', (e: FederatedPointerEvent) => e.stopPropagation());
  root.addChild(backdrop);

  // ── State ───────────────────────────────────────────────
  const forgeLevel = manager.buildings.getForgeLevel();
  const slotsNeeded = forgeLevel <= 1 ? 3 : 2;
  const inputSlots: (ScoredItem | null)[] = [];
  for (let i = 0; i < slotsNeeded; i++) inputSlots.push(null);

  // Tooltip state
  let forgeTip: Container | null = null;
  function showForgeTip(item: ScoredItem, cx: number, cy: number) {
    hideForgeTip();
    let result: { tip: Container; width: number; height: number } | null = null;
    if (isWeaponItem(item)) {
      const def = getWeaponById(item.id);
      if (def) result = createWeaponTooltip(def, item);
    } else {
      const def = getUpgradeById(item.id);
      if (def) result = createUpgradeTooltip(def, item);
    }
    if (result) {
      positionTooltip(result.tip, result.height, result.width, cx, cy, screenW, screenH);
      root.addChild(result.tip);
      forgeTip = result.tip;
    }
  }
  function hideForgeTip() {
    if (forgeTip) { root.removeChild(forgeTip); forgeTip.destroy({ children: true }); forgeTip = null; }
  }

  // Resolved category/tier lock — set once first item is placed
  let lockCategory: string | null = null;
  let lockTier: number | null = null;
  let lockIsWeapon: boolean | null = null;

  // ── Panel container ─────────────────────────────────────
  const panel = new Container();
  const panelX = Math.round((screenW - PANEL_W) / 2);
  const panelY = Math.round((screenH - PANEL_H) / 2);
  panel.position.set(panelX, panelY);
  root.addChild(panel);

  // ── Reusable layer containers ───────────────────────────
  // We destroy and recreate children inside `rebuild` so that
  // inventory changes are reflected without closing the overlay.
  let content: Container | null = null;
  // Track the masked inventory container so we can detach the mask before
  // destruction — PixiJS does not auto-null `.mask` refs on destroy, and
  // keeping the reference can keep the mask Graphics alive if destroy order
  // ever changes (e.g. a future caller destroying the mask first).
  let maskedInvContainer: Container | null = null;
  // Latest scroll-drag handle — destroyed on rebuild and on overlay dispose
  // so its globalpointermove listener is deregistered (Pixi's destroy path
  // does not decrement the EventSystem refcount for global events).
  let activeScrollDrag: ScrollDragHandle | null = null;

  function rebuild() {
    if (activeScrollDrag) { activeScrollDrag.destroy(); activeScrollDrag = null; }
    if (content) {
      if (maskedInvContainer) { maskedInvContainer.mask = null; maskedInvContainer = null; }
      panel.removeChild(content);
      content.destroy({ children: true });
    }
    content = new Container();
    panel.addChild(content);

    // ── Panel background ────────────────────────────────
    const bg = new Graphics();
    drawBevelPanel(bg, 0, 0, PANEL_W, PANEL_H, {
      fill: 0x0c0e14,
      border: 0x1a2030,
      radius: 8,
      depth: 4,
      rivets: true,
      accentEdge: FORGE_COLOR,
    });
    content.addChild(bg);

    // ── Header band ─────────────────────────────────────
    const hdrBand = new Graphics();
    drawHeaderBand(hdrBand, 12, 10, PANEL_W - 24, FORGE_COLOR, 0x0c0e14, 28);
    content.addChild(hdrBand);

    // Stars for forge level
    let stars = '';
    for (let i = 0; i < 3; i++) stars += i < forgeLevel ? '\u2605' : '\u2606';
    const titleText = new Text({
      text: `FORGE  ${stars}`,
      style: {
        fontFamily: 'Gugi',
        fontSize: 17,
        fill: 0xffeedd,
        dropShadow: GUGI_SHADOW,
      },
    });
    titleText.anchor.set(0, 0.5);
    titleText.position.set(22, 24);
    content.addChild(titleText);

    // Credits display
    const creditsText = new Text({
      text: `Cr  ${manager.money.toLocaleString()}`,
      style: { fontFamily: 'Offside', fontSize: 14, fill: 0xffcc44 },
    });
    creditsText.anchor.set(1, 0.5);
    creditsText.position.set(PANEL_W - 50, 24);
    content.addChild(creditsText);

    // ── Close button ──────────────────────────────────────
    buildCloseButton(content, PANEL_W - 42, 6, ACCENT, ACCENT_DARK, onClose, { hoverTint: false });

    // ── Merge area ──────────────────────────────────────
    const mergeY = 52;

    // Subtitle
    const ruleText = forgeLevel <= 1
      ? 'Combine 3 same-type, same-tier items (avg score)'
      : forgeLevel === 2
        ? 'Combine 2 same-type, same-tier items (avg +3)'
        : 'Combine 2 items (best score +5)';
    const subtitle = new Text({
      text: ruleText,
      style: { fontFamily: 'Offside', fontSize: 12, fill: 0x667788 },
    });
    subtitle.anchor.set(0.5, 0);
    subtitle.position.set(PANEL_W / 2, mergeY);
    content.addChild(subtitle);

    // Compute positions for input slots, plus symbols, arrow, and output slot
    const slotRowY = mergeY + 24;
    // Total width: slotsNeeded * SLOT_SIZE + (slotsNeeded-1) * "+" gap + arrow gap + output slot
    const plusGap = 36;    // space for "+" symbol
    const arrowGap = 48;  // space for arrow symbol
    const inputRowW = slotsNeeded * SLOT_SIZE + (slotsNeeded - 1) * plusGap;
    const totalW = inputRowW + arrowGap + SLOT_SIZE;
    const startX = Math.round((PANEL_W - totalW) / 2);

    // Draw input slots
    for (let i = 0; i < slotsNeeded; i++) {
      const sx = startX + i * (SLOT_SIZE + plusGap);
      const slotBg = new Graphics();
      drawInsetRect(slotBg, 0, 0, SLOT_SIZE, SLOT_SIZE, 5, 0x0a0c12, 0x222838, 0.7);
      slotBg.position.set(sx, slotRowY);
      content.addChild(slotBg);

      const item = inputSlots[i];
      if (item) {
        // Render the placed item
        const cell = buildItemCell(item, SLOT_SIZE);
        cell.position.set(sx, slotRowY);
        cell.eventMode = 'static';
        cell.cursor = 'pointer';
        const slotIndex = i;
        cell.on('pointerdown', () => {
          returnToInventory(slotIndex);
        });
        cell.on('pointerenter', () => showForgeTip(item, panelX + sx, panelY + slotRowY));
        cell.on('pointerleave', () => hideForgeTip());
        content.addChild(cell);
      } else {
        // Empty slot indicator
        const emptyLabel = new Text({
          text: '+',
          style: { fontFamily: 'Gugi', fontSize: 28, fill: 0x334455 },
        });
        emptyLabel.anchor.set(0.5);
        emptyLabel.position.set(sx + SLOT_SIZE / 2, slotRowY + SLOT_SIZE / 2);
        content.addChild(emptyLabel);
      }

      // Plus symbol between slots
      if (i < slotsNeeded - 1) {
        const plusX = sx + SLOT_SIZE + plusGap / 2;
        const plusLabel = new Text({
          text: '+',
          style: { fontFamily: 'Gugi', fontSize: 22, fill: FORGE_COLOR },
        });
        plusLabel.anchor.set(0.5);
        plusLabel.position.set(plusX, slotRowY + SLOT_SIZE / 2);
        content.addChild(plusLabel);
      }
    }

    // Arrow
    const arrowX = startX + inputRowW + arrowGap / 2;
    const arrowLabel = new Text({
      text: '\u2192',
      style: { fontFamily: 'Gugi', fontSize: 26, fill: FORGE_COLOR },
    });
    arrowLabel.anchor.set(0.5);
    arrowLabel.position.set(arrowX, slotRowY + SLOT_SIZE / 2);
    content.addChild(arrowLabel);

    // Output slot
    const outputX = startX + inputRowW + arrowGap;
    const outputBg = new Graphics();
    drawInsetRect(outputBg, 0, 0, SLOT_SIZE, SLOT_SIZE, 5, 0x0e1008, 0x334422, 0.7);
    outputBg.position.set(outputX, slotRowY);
    content.addChild(outputBg);

    // Determine if merge is valid
    const allFilled = inputSlots.every(s => s !== null);
    const outputItem = allFilled ? resolveOutputItem() : null;

    if (outputItem) {
      const outCell = buildItemCell(outputItem, SLOT_SIZE);
      outCell.position.set(outputX, slotRowY);
      outCell.eventMode = 'static';
      outCell.on('pointerenter', () => showForgeTip(outputItem, panelX + outputX, panelY + slotRowY));
      outCell.on('pointerleave', () => hideForgeTip());
      content.addChild(outCell);
    } else {
      const qMark = new Text({
        text: '?',
        style: { fontFamily: 'Gugi', fontSize: 28, fill: 0x334455 },
      });
      qMark.anchor.set(0.5);
      qMark.position.set(outputX + SLOT_SIZE / 2, slotRowY + SLOT_SIZE / 2);
      content.addChild(qMark);
    }

    // ── MERGE button ────────────────────────────────────
    const mergeEnabled = outputItem !== null;
    const mergeBtnW = 140;
    const mergeBtnH = 34;
    const mergeBtnX = Math.round((PANEL_W - mergeBtnW) / 2);
    const mergeBtnY = slotRowY + SLOT_SIZE + 14;

    const mergeBtn = new Container();
    const mergeBg = new Graphics();
    const mergeColor = mergeEnabled ? FORGE_COLOR : 0x222838;
    draw3DButton(mergeBg, mergeBtnW, mergeBtnH, mergeColor, { radius: 5, depth: 3 });
    mergeBtn.addChild(mergeBg);

    const mergeLbl = new Text({
      text: 'MERGE',
      style: {
        fontFamily: 'Gugi',
        fontSize: 15,
        fill: mergeEnabled ? 0xffeedd : 0x445566,
        dropShadow: GUGI_SHADOW,
      },
    });
    mergeLbl.anchor.set(0.5);
    mergeLbl.position.set(mergeBtnW / 2, mergeBtnH / 2);
    mergeBtn.addChild(mergeLbl);
    mergeBtn.position.set(mergeBtnX, mergeBtnY);

    if (mergeEnabled) {
      mergeBtn.eventMode = 'static';
      mergeBtn.cursor = 'pointer';
      mergeBtn.on('pointerdown', () => {
        performMerge(outputItem);
      });
    }
    content.addChild(mergeBtn);

    // ── Inventory section ───────────────────────────────
    const invHeaderY = mergeBtnY + mergeBtnH + 18;
    const invBand = new Graphics();
    drawHeaderBand(invBand, 12, invHeaderY, PANEL_W - 24, ACCENT_DIM, 0x0c0e14, 22);
    content.addChild(invBand);

    const invTitle = new Text({
      text: 'INVENTORY',
      style: { fontFamily: 'Gugi', fontSize: 12, fill: 0xccbbaa, dropShadow: GUGI_SHADOW },
    });
    invTitle.anchor.set(0, 0.5);
    invTitle.position.set(22, invHeaderY + 11);
    content.addChild(invTitle);

    const invStartY = invHeaderY + 28;
    const invAreaH = PANEL_H - invStartY - 12;

    // Inventory inset background
    const invBg = new Graphics();
    drawInsetRect(invBg, 12, invStartY, PANEL_W - 24 - SCROLLBAR_WIDTH - 4, invAreaH, 4, 0x080a10, 0x1a2030, 0.5);
    content.addChild(invBg);

    // Gather all unequipped items
    const allItems: ScoredItem[] = [
      ...manager.upgrades.inventory,
      ...manager.upgrades.weaponInventory,
    ];

    // Build inventory grid
    const gridPadX = 20;
    const gridPadY = 6;
    const gridStartX = gridPadX;
    const gridStartY = invStartY + gridPadY;
    const cellStep = CELL_SIZE + CELL_GAP;

    // Scrollable container for inventory
    const invContainer = new Container();
    invContainer.position.set(0, 0);
    content.addChild(invContainer);

    // Create a mask for the inventory area
    const invMask = new Graphics();
    const invVisibleW = PANEL_W - 24 - SCROLLBAR_WIDTH - 4;
    invMask.rect(12, invStartY, invVisibleW, invAreaH);
    invMask.fill(0xffffff);
    content.addChild(invMask);
    invContainer.mask = invMask;
    maskedInvContainer = invContainer;

    const totalContentH = Math.ceil(allItems.length / INV_COLS) * (CELL_SIZE + CELL_GAP);
    const totalRows = Math.ceil(allItems.length / INV_COLS);
    const visibleRows = Math.floor(invAreaH / cellStep);
    const maxScroll = Math.max(0, (totalRows - visibleRows) * cellStep);
    let scrollOffset = 0;

    // Transparent hit-area inside invContainer so pointer/wheel events fire on
    // gaps between cells. Cell events also bubble to invContainer.
    const listHit = new Graphics();
    listHit.rect(12, invStartY, invVisibleW, Math.max(invAreaH, totalContentH));
    listHit.fill({ color: 0x000000, alpha: 0.001 });
    listHit.eventMode = 'static';
    invContainer.addChildAt(listHit, 0);

    const scrollBar = createScrollBar({
      trackHeight: invAreaH,
      contentHeight: totalContentH,
      visibleHeight: invAreaH,
      onChange: (frac) => {
        scrollOffset = Math.round(frac * maxScroll);
        applyScroll();
      },
    });
    scrollBar.container.position.set(PANEL_W - 12 - SCROLLBAR_WIDTH, invStartY);
    content.addChild(scrollBar.container);

    function buildInventoryGrid() {
      // Build cells once — positioned at their natural grid location
      invContainer.removeChildren();

      for (let idx = 0; idx < allItems.length; idx++) {
        const item = allItems[idx]!;
        const col = idx % INV_COLS;
        const row = Math.floor(idx / INV_COLS);
        const cx = gridStartX + col * cellStep;
        const cy = gridStartY + row * cellStep;

        const cell = buildItemCell(item, CELL_SIZE);
        cell.position.set(cx, cy);

        // Dim items that don't match the current lock
        const matches = itemMatchesLock(item);
        if (!matches) {
          cell.alpha = 0.4;
        } else {
          cell.eventMode = 'static';
          cell.cursor = 'pointer';
          cell.on('pointertap', () => {
            if (scrollDrag.wasDragging()) return;
            placeInSlot(item);
          });
        }

        // Tooltip on hover (works even for dimmed items)
        cell.eventMode = 'static';
        cell.on('pointerenter', () => showForgeTip(item, panelX + cx, panelY + cy - scrollOffset));
        cell.on('pointerleave', () => hideForgeTip());

        invContainer.addChild(cell);
      }
    }
    buildInventoryGrid();

    function applyScroll() {
      // Shift the entire container — the mask handles clipping
      invContainer.position.y = -scrollOffset;
    }

    // Unified wheel + click-drag + touch-drag scroll. Events from cells bubble
    // to invContainer; listHit covers gaps.
    invContainer.eventMode = 'static';
    const scrollDrag = attachScrollDrag({
      hitArea: invContainer,
      onDelta: (dy: number) => {
        if (maxScroll <= 0) return;
        scrollOffset = Math.max(0, Math.min(maxScroll, scrollOffset + dy));
        applyScroll();
        scrollBar.setFraction(scrollOffset / maxScroll);
      },
    });
    activeScrollDrag = scrollDrag;
  }

  // ── Helper: build an item cell (upgrade or weapon) ────
  function buildItemCell(item: ScoredItem, size: number): Container {
    if (isWeaponItem(item)) {
      const def = getWeaponById(item.id);
      if (def) return buildWeaponCell(def, item, size);
    } else {
      const def = getUpgradeById(item.id);
      if (def) return buildUpgradeCell(def, item, size);
    }
    // Fallback — unknown item
    const c = new Container();
    const g = new Graphics();
    drawInsetRect(g, 0, 0, size, size, 4, 0x161a22, 0x333333, 0.5);
    c.addChild(g);
    return c;
  }

  // ── Helper: get category and tier of an item ──────────
  function getItemInfo(item: ScoredItem): { category: string; tier: number; isWeapon: boolean } | null {
    if (isWeaponItem(item)) {
      const def = getWeaponById(item.id);
      if (!def) return null;
      return { category: def.category, tier: def.tier, isWeapon: true };
    } else {
      const def = getUpgradeById(item.id);
      if (!def) return null;
      return { category: def.category, tier: def.tier, isWeapon: false };
    }
  }

  // Weapons cap at tier 5 in forges below level 3. Tiers 6–10 are
  // mythic — they only combine at the fully-upgraded Forge (lv3).
  // Upgrades keep their legacy tier-10 ceiling regardless of forge level.
  const weaponMaxTier = forgeLevel >= 3 ? 10 : 5;
  const maxTierFor = (isWeapon: boolean): number => isWeapon ? weaponMaxTier : 10;

  // ── Helper: check if item matches current lock ────────
  function itemMatchesLock(item: ScoredItem): boolean {
    // No lock yet — everything is valid (unless it's max-tier)
    if (lockCategory === null || lockTier === null || lockIsWeapon === null) {
      // Still need to check max tier
      const info = getItemInfo(item);
      if (!info) return false;
      return info.tier < maxTierFor(info.isWeapon);
    }

    const info = getItemInfo(item);
    if (!info) return false;
    if (info.isWeapon !== lockIsWeapon) return false;
    if (info.category !== lockCategory) return false;
    if (info.tier !== lockTier) return false;

    // Don't allow placing the same exact reference that's already in a slot
    // (but do allow same id+quality since there may be duplicates)
    return true;
  }

  // ── Place an item into the next empty input slot ──────
  function placeInSlot(item: ScoredItem) {
    const emptyIdx = inputSlots.indexOf(null);
    if (emptyIdx < 0) return;

    // Items carry generated names, so each (id, score, name) triple is a unique
    // inventory entry. Match on all three to avoid double-placing one source item.
    const invItems = isWeaponItem(item)
      ? manager.upgrades.weaponInventory
      : manager.upgrades.inventory;
    const totalInInv = invItems.filter(i => i.id === item.id && i.score === item.score && i.name === item.name).length;
    const alreadyPlaced = inputSlots.filter(s => s !== null && s.id === item.id && s.score === item.score && s.name === item.name).length;
    if (alreadyPlaced >= totalInInv) return; // no more of this item available

    const info = getItemInfo(item);
    if (!info) return;

    // Check max tier
    if (info.tier >= maxTierFor(info.isWeapon)) return;

    // Set lock on first placement
    if (lockCategory === null) {
      lockCategory = info.category;
      lockTier = info.tier;
      lockIsWeapon = info.isWeapon;
    }

    // Verify match
    if (info.category !== lockCategory || info.tier !== lockTier || info.isWeapon !== lockIsWeapon) return;

    inputSlots[emptyIdx] = { id: item.id, score: item.score, name: item.name };
    rebuild();
  }

  // ── Return an item from a slot back to inventory ──────
  function returnToInventory(slotIndex: number) {
    if (!inputSlots[slotIndex]) return;
    inputSlots[slotIndex] = null;

    // If all slots are now empty, clear the lock
    if (inputSlots.every(s => s === null)) {
      lockCategory = null;
      lockTier = null;
      lockIsWeapon = null;
    }

    rebuild();
  }

  // ── Resolve the output item from current inputs ───────
  function resolveOutputItem(): ScoredItem | null {
    if (!inputSlots.every(s => s !== null)) return null;

    const firstItem = inputSlots[0]!;
    const info = getItemInfo(firstItem);
    if (!info) return null;

    // Output score scales with forge level.
    const scores = inputSlots.filter((s): s is ScoredItem => s !== null).map(s => s.score);
    let outputScore: number;
    if (forgeLevel >= 3) {
      outputScore = Math.min(100, Math.max(...scores) + 5);
    } else if (forgeLevel === 2) {
      outputScore = Math.min(100, Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) + 3);
    } else {
      outputScore = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
    }

    // Find the output definition (tier + 1 in the same category).
    let outputId: string | undefined;
    let outputCategory: string | undefined;
    if (info.isWeapon) {
      const inputDef = getWeaponById(firstItem.id);
      if (!inputDef) return null;
      const outputDef = ALL_WEAPONS.find(w => w.category === inputDef.category && w.tier === inputDef.tier + 1);
      outputId = outputDef?.id;
      outputCategory = outputDef?.category;
    } else {
      const inputDef = getUpgradeById(firstItem.id);
      if (!inputDef) return null;
      const outputDef = ALL_UPGRADES.find(u => u.category === inputDef.category && u.tier === inputDef.tier + 1);
      outputId = outputDef?.id;
      outputCategory = outputDef?.category;
    }

    if (!outputId || !outputCategory) return null;
    const kind = info.isWeapon ? 'weapon' : 'upgrade';
    const outputName = generateItemName(kind, outputCategory, outputScore);
    return { id: outputId, score: outputScore, name: outputName };
  }

  // ── Perform the merge ─────────────────────────────────
  function performMerge(output: ScoredItem) {
    // Remove input items from inventory
    for (const slot of inputSlots) {
      if (!slot) continue;
      if (isWeaponItem(slot)) {
        manager.upgrades.removeWeaponFromInventory(slot);
      } else {
        manager.upgrades.removeFromInventory(slot);
      }
    }

    // Add output item
    if (isWeaponItem(output)) {
      manager.upgrades.addWeaponToInventory(output);
      const outDef = getWeaponById(output.id);
      if (outDef) {
        trackForge(manager.achievements, outDef.tier, outDef.category);
      }
    } else {
      manager.upgrades.addToInventory(output);
    }

    // Clear slots and lock
    for (let i = 0; i < inputSlots.length; i++) inputSlots[i] = null;
    lockCategory = null;
    lockTier = null;
    lockIsWeapon = null;

    rebuild();
  }

  // ── Initial build ─────────────────────────────────────
  rebuild();

  return {
    container: root,
    update: (_dt: number) => {
      // No animations needed for now
    },
    dispose: () => {
      if (activeScrollDrag) { activeScrollDrag.destroy(); activeScrollDrag = null; }
    },
  };
}
